import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import Button from './Button';
import ImageInquiryDialog from './ImageInquiryDialog';
import ImagePreviewWithRequest from './ImagePreviewWithRequest';
import closeIcon from '@/assets/close.svg';
import magnifierIcon from '@/assets/cursor_magnifier.svg';
import triangleIcon from '@/assets/triangle_indicator.svg';
import { formatCurrency, formatDateForTooltip } from '@/utils/formatUtils';
import { getPsaListings } from '@/api/products';
import { createListingInquiry } from '@/api/listing';
import { showSuccessToast, showErrorToast } from '@/utils/toastManager';
import OfficialTag from '@/components/OfficialTag';
import useAuthStore from '@/stores/authStore';
import { redirectToLogin } from '@/utils/authUtils';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

const PSAInventoryModal = ({ isOpen, onClose, productId, psa, psaLabel, inventory, onPurchase }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = useAuthStore((state) => state.accessToken);
  const [selectedListing, setSelectedListing] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);

  // Fetch PSA listings with infinite query
  const { 
    data: listingsData, 
    isLoading, 
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['psaListings', productId, psa],
    queryFn: ({ pageParam = null }) => getPsaListings(productId, { psa, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: null,
    enabled: isOpen && !!productId && !!psa,
  });

  const listings = listingsData?.pages?.flatMap(page => page.data) || [];

  // Mutation for creating listing inquiry
  const inquiryMutation = useMutation({
    mutationFn: ({ listingId, types }) => createListingInquiry(listingId, types),
    onSuccess: () => {
      showSuccessToast(t('psaInventory.requestDialog.requestSent'));
      setIsRequestDialogOpen(false);
    },
    onError: (error) => {
      showErrorToast(error.message || t('common.api_error'));
    }
  });

  // Reset selected listing when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedListing(null);
      setCurrentImageIndex(0);
    }
  }, [isOpen]);


  // Get available images for preview from a listing
  const getListingImages = (listing) => {
    const images = [];
    if (listing.images?.front?.url) images.push(listing.images.front);
    if (listing.images?.back?.url) images.push(listing.images.back);
    if (listing.images?.frontTopLeft?.url) images.push(listing.images.frontTopLeft);
    if (listing.images?.frontTopRight?.url) images.push(listing.images.frontTopRight);
    if (listing.images?.frontBottomLeft?.url) images.push(listing.images.frontBottomLeft);
    if (listing.images?.frontBottomRight?.url) images.push(listing.images.frontBottomRight);
    if (listing.images?.damage?.url) images.push(listing.images.damage);
    if (listing.images?.extras?.length) {
      listing.images.extras.forEach(extra => {
        if (extra?.url) images.push(extra);
      });
    }
    return images;
  };


  if (!isOpen) return null;

  const handleImageClick = (listing) => {
    setSelectedListing(listing);
    setCurrentImageIndex(0);
  };

  const handleRequestImages = () => {
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    if (selectedListing) {
      setIsRequestDialogOpen(true);
    }
  };

  const handleRequestSubmit = (types) => {
    if (selectedListing) {
      inquiryMutation.mutate({
        listingId: selectedListing.id,
        types
      });
    }
  };

  const formatSellerName = (nickname) => {
    return `出品者：${nickname}`;
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 flex justify-end items-center z-50">
      {/* Left side - Large image view using ImagePreviewWithRequest */}
      {selectedListing ? (
        <ImagePreviewWithRequest
          images={getListingImages(selectedListing).map(img => img.url)}
          initialIndex={currentImageIndex}
          onClose={() => setSelectedListing(null)}
          onRequestImages={handleRequestImages}
          showRequestFeature={true}
        />
      ) : (
        <div className="flex-1 self-stretch relative overflow-hidden"></div>
      )}

      {/* Right side - Listing panel */}
      <div className="w-[480px] h-full self-stretch pb-6 relative bg-white flex flex-col justify-start items-start">
        {/* Header */}
        <div className="self-stretch p-6 flex justify-center items-center relative">
          <div className="flex justify-start items-center gap-2">
            <div className="justify-center">
              <span className="text-[#111111] text-lg font-bold">
                {psaLabel} 
              </span>
              <span className="text-[#111111] text-lg font-normal">
                {t('psaInventory.stockRemaining', { count: inventory })}
              </span>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="absolute right-6 w-8 h-8  flex items-center justify-center"
          >
              <img src={closeIcon} alt="close" className="w-7 h-7" />
          </button>
        </div>

        {/* Listings */}
        <div className="w-full h-full px-6 flex-1 flex flex-col justify-start items-start overflow-y-auto">
          {isLoading ? (
            <div className="self-stretch py-10 flex justify-center items-center">
              <div className="text-[#666666] text-sm">{t('common.loading')}</div>
            </div>
          ) : error ? (
            <div className="self-stretch py-10 flex justify-center items-center">
              <div className="text-[#ff5501] text-sm">{t('common.error')}</div>
            </div>
          ) : listings.length === 0 ? (
            <div className="self-stretch py-10 flex justify-center items-center">
              <div className="text-[#666666] text-sm">{t('psaInventory.noStock')}</div>
            </div>
          ) : (
            <div className="self-stretch flex flex-col">
              {listings.map((listing) => {
                const isSoldOut = listing.status !== 'ON_SALE';
                const isSelected = selectedListing?.id === listing.id;
                const availableImages = getListingImages(listing);
                const hasImage = availableImages.length > 0;
                
                return (
                  <div 
                    key={listing.id} 
                    className="self-stretch py-5 border-b border-[#eeeeee] flex justify-start items-center relative"
                  >
                    {/* Product image */}
                    <button
                      className={`w-16 h-16 relative bg-white border border-[#DDDDDD] rounded overflow-hidden cursor-pointer`}
                      onClick={() => handleImageClick(listing)}
                    >
                      <img 
                        className={`w-[62px] h-[62px] left-[1px] top-[1px] object-contain absolute mix-blend-darken rounded ${!hasImage ? 'blur-sm' : ''}`}
                        src={getProductThumbnailUrl(availableImages[0]?.url || listing.product?.images?.[0]?.url) || '/placeholder-image.jpg'}
                        alt="Product"
                      />
                      {isSoldOut && (
                        <div className="w-[62px] h-[62px] left-[1px] top-[1px] absolute bg-white/0 backdrop-blur-[2px]" />
                      )}
                      <div className="w-5 h-5 left-[44px] top-[44px] absolute bg-white rounded-tl-lg rounded-br overflow-hidden">
                        <img src={magnifierIcon} alt="magnify" className="w-4.5 h-4.5 left-[0px] top-[0px] absolute" />
                      </div>
                    </button>

                    {/* Listing info */}
                      <div className="flex-1 pl-4 flex justify-start items-center gap-4">
                      <div className="flex flex-col justify-start items-start gap-1">
                        <div className="self-stretch flex justify-start items-center gap-1">
                          <div className="justify-center text-[#111111] text-2xl font-bold font-['Roboto'] leading-9">
                            {formatCurrency(listing.price, '¥ ')}
                          </div>
                          {listing.isOfficial && (
                            <OfficialTag />
                          )}
                        </div>
                        <div className="self-stretch flex justify-start items-center gap-1">
                          <div className="justify-center text-[#aaaaaa] text-sm font-normal">
                            {formatDateForTooltip(listing.createdAt)} 
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Purchase button */}
                    <Button
                      variant="primary"
                      size="m"
                      className="w-16 h-9 px-[18px] bg-[#111111] rounded-3xl"
                      onClick={() => onPurchase && onPurchase(listing)}
                      disabled={isSoldOut}
                    >
                      <div className="h-9 flex justify-center items-center gap-1.5">
                        <div className="text-center justify-center text-white text-sm font-normal">
                          {t('psaInventory.purchase')}
                        </div>
                      </div>
                    </Button>
                    
                    {/* Selected item indicator */}
                    {isSelected && (
                      <div className="absolute left-[-24px] top-1/2 transform -translate-y-1/2">
                        <img src={triangleIcon} alt="selected indicator" className="w-3 h-5" />
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Load More Button */}
              {hasNextPage && (
                <div className="self-stretch py-4 flex justify-center items-center">
                  <Button
                    variant="default"
                    size="m"
                    onClick={handleLoadMore}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? t('common.loading') : t('home.actions.load_more')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Request Type Dialog */}
      <ImageInquiryDialog
        isOpen={isRequestDialogOpen}
        onClose={() => setIsRequestDialogOpen(false)}
        onSubmit={handleRequestSubmit}
        loading={inquiryMutation.isPending}
        cardType="PSA_SINGLE_CARD"
      />
    </div>
  );
};

PSAInventoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  productId: PropTypes.string,
  psa: PropTypes.string,
  psaLabel: PropTypes.string,
  inventory: PropTypes.number,
  onPurchase: PropTypes.func
};

export default PSAInventoryModal;
